import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
// import { useSelector } from "react-redux";
import { Redirect, Route, Switch, useHistory, useParams } from "react-router-dom";
import { EventsState } from "../../../store/types";
// import { EventsState } from "../../../store/types";
import { Dictionary, IHomepageMainNavItems } from "../../../types/working-model";
import { eventPath } from "../utils";
import MarketingRegisteredAgenda from "./marketing-registered-agenda/marketing-registered-agenda";
import { MarketingRegisteredPage as MarketingRegisteredHome } from "./marketing-registered-page";
import useTranslationModules from "../../../i18n/useTranslationModules";
import useRedirectLanguage from "./use-redirect-language";
import { getStorageItem } from "../../../utils/local-storage";

interface MarketingRegisteredRoutesProps {
	debug?: boolean;
}

const MarketingRegisteredRoutes: React.FC<MarketingRegisteredRoutesProps> = ({ debug }) => {
	const {
		LiveEventReducer: {
			eventBundle
		}
	} = useSelector((state: EventsState) => state);
	const history = useHistory();

	useRedirectLanguage({ event: eventBundle?.event });

	const eventMainNavItems = eventBundle?.homepage?.event_main_nav_items;
	const { uuid, eventName, language }: any = useParams();

	//check to make sure language is in translated list, if not, push them to the default language
	//in this case english is currently locked as the homepage and registration default
	const notValidTranslation = !eventBundle?.homepage?.languages.includes(language);

	// if stored language, get it and check if it's in the translated list
	const storedLanguage = eventBundle?.event ? getStorageItem(`event.${eventBundle.event}.language`) : null;
	const validStoredTranslation = storedLanguage && eventBundle?.homepage?.languages.includes(storedLanguage);

	// first try and use the stored language, else go back to the params language
	// this will work because if a stored langauge exists, we are always forcing a redirect to that language
	useTranslationModules({
		language: validStoredTranslation && storedLanguage
			? storedLanguage
			: notValidTranslation
				? "en"
				: language
	});

	useEffect(() => {
		if (notValidTranslation && (eventName || uuid)) {
			history.replace(`/${eventPath(eventName, uuid)}/en/home`);
		}
	}, [history, eventName, uuid, notValidTranslation]);

	const RouteMap = useMemo(() => {
		let routeMap: Dictionary = {};

		// TODO: Post MVP when we do custom tab names decide if we want to use custom_name or 
		// name for the endpoint. if custom_name, update marketing-nav to use that as well.
		const customRouteMap = eventMainNavItems?.reduce((a: { [key: string]: string }, c: IHomepageMainNavItems) => {
			a[c.name] = `${debug ? '/event/:uuid' : '/:eventName'}/:language/${c.name.toLowerCase()}`;
			return a;
		}, {});

		let defaultRouteMap: Dictionary = {};

		if (!debug) {
			defaultRouteMap = {
				Home: `/:eventName/:language/home`,
				Sessions: `/:eventName/:language/sessions`,
				People: `/:eventName/:language/people`,
				Landing: `/:eventName/:language`,
			};
		} else {
			defaultRouteMap = {
				Home: `/event/:uuid/:language/home`,
				Sessions: `/event/:uuid/:language/sessions`,
				People: `/event/:uuid/:language/people`,
				Landing: `/event/:uuid/:language`,
			};
		}

		if (customRouteMap) {
			routeMap = customRouteMap;
		} else {
			routeMap = defaultRouteMap;
		}

		return routeMap;
	}, [debug, eventMainNavItems]);

	function getComponent(tabName: string) {
		switch (tabName.toUpperCase()) {
			case 'HOME': return <MarketingRegisteredHome />;
			case 'SESSIONS': return <MarketingRegisteredAgenda />;
			default: return <MarketingRegisteredHome />;
		}
	}

	const MarketingRegisteredComponentMap = useMemo(() => {
		if (eventMainNavItems) return eventMainNavItems?.reduce((a: { [key: string]: JSX.Element }, c: IHomepageMainNavItems) => {
			a[c.name] = getComponent(c.name);
			return a;
		}, {});
		return {};
	}, [eventMainNavItems]);

	return (
		<Switch>
			{/* TODO HTML */}
			{
				eventMainNavItems && eventMainNavItems?.map((navItem) => (
					<Route key={navItem.uuid} exact={true} path={RouteMap[navItem.name]}>
						{MarketingRegisteredComponentMap[navItem.name]}
					</Route>
				))
			}
			{/*  default to home */}
			<Route exact={true} path={RouteMap.Home}>
				<MarketingRegisteredHome />
			</Route>
			<Route exact={true} path={RouteMap.Landing}>
				<Redirect to={`/${eventPath(eventName, uuid)}/${language}/home`} />
			</Route>
		</Switch>
	);
};

export default MarketingRegisteredRoutes;